import { Select } from '@amzn/awsui-components-react';
import { availablePresets, type WindowPresetId } from './windowPresets';

interface Props {
  /** Clock the presets resolve against; the page re-reads it on user actions. */
  today: Date;
  selectedId: WindowPresetId;
  onChange: (id: WindowPresetId) => void;
  /**
   * Called when the list is about to be shown. The page re-reads the clock here so
   * the options are current at the moment they are read — without it, a tab opened
   * before a quarter boundary would offer a list that can never include the quarter
   * the viewer is now in.
   */
  onOpen?: () => void;
}

/**
 * The window dropdown. Presets live in `windowPresets.ts`; this only renders them,
 * because the operation takes explicit dates and the preset list is a layout
 * decision the backend has no opinion on.
 *
 * The dropdown list shows each choice's dates; the closed trigger deliberately does
 * NOT (`triggerVariant="option"` would, but it makes the trigger two lines tall and
 * breaks the header row's alignment). So the open list is the only place the dates
 * appear — the page used to also echo the applied window under the header, but that
 * line was removed, and the only surface that still names a window is the error alert
 * (which needs it, because the numbers on screen can be a stale window's).
 */
export function WindowSelect({ today, selectedId, onChange, onOpen }: Props) {
  const presets = availablePresets(today);
  // Defensive only: the page passes an id it has already checked resolves against
  // this same anchor, so the fallback is unreachable. It must NOT carry meaning —
  // an independent fallback here would disagree with the page's and label one
  // window's numbers with another window's name.
  const selected = presets.find((p) => p.id === selectedId) ?? presets[0];

  return (
    <Select
      selectedOption={{
        value: selected.id,
        label: selected.label,
        description: `${selected.startDate} → ${selected.endDate}`,
      }}
      options={presets.map((p) => ({
        value: p.id,
        label: p.label,
        description: `${p.startDate} → ${p.endDate}`,
      }))}
      onChange={({ detail }) => onChange(detail.selectedOption.value as WindowPresetId)}
      onFocus={onOpen}
      ariaLabel="时间窗口"
    />
  );
}
